import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';

/**
 * Diagnostic utility to check if the current user has permissions to edit influencers
 */
export async function checkInfluencerEditPermissions(): Promise<{
  canEdit: boolean;
  userId: string | undefined;
  role: string | null;
  permissions: Record<string, boolean> | null;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        canEdit: false,
        userId: undefined,
        role: null,
        permissions: null,
        error: 'No authenticated user',
      };
    }

    // Check user roles and permissions
    const { data: userRole, error } = await supabase
      .from('user_roles')
      .select('role, can_edit_influencers, can_edit_campaigns, can_edit_clients')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      log.error('Error checking permissions:', error);
      return {
        canEdit: false,
        userId: user.id,
        role: null,
        permissions: null,
        error: error.message,
      };
    }

    if (!userRole) {
      return {
        canEdit: false,
        userId: user.id,
        role: null,
        permissions: null,
        error: 'No user role found',
      };
    }

    return {
      canEdit: userRole.can_edit_influencers || false,
      userId: user.id,
      role: userRole.role,
      permissions: {
        can_edit_influencers: userRole.can_edit_influencers,
        can_edit_campaigns: userRole.can_edit_campaigns,
        can_edit_clients: userRole.can_edit_clients,
      },
    };
  } catch (error) {
    log.error('Unexpected error in checkInfluencerEditPermissions:', error);
    return {
      canEdit: false,
      userId: undefined,
      role: null,
      permissions: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Test function to verify an influencer update would work
 */
export async function testInfluencerUpdate(influencerId: string, testValue: number): Promise<{
  success: boolean;
  error?: string;
  details?: any;
}> {
  try {
    // First check permissions
    const permCheck = await checkInfluencerEditPermissions();

    if (!permCheck.canEdit) {
      return {
        success: false,
        error: 'User does not have edit permissions',
        details: permCheck,
      };
    }

    // Try to update the influencer
    const { data, error } = await supabase
      .from('influencers')
      .update({ default_commission_pct: testValue, updated_at: new Date().toISOString() })
      .eq('id', influencerId)
      .select()
      .single();

    if (error) {
      return {
        success: false,
        error: error.message,
        details: { code: error.code, hint: error.hint },
      };
    }

    return {
      success: true,
      details: {
        updated_commission: data.default_commission_pct,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Log diagnostic information about the current session
 */
export async function logSessionDiagnostics(): Promise<void> {
  console.group('=== Session Diagnostics ===');

  const { data: { session } } = await supabase.auth.getSession();
  log.debug('Session exists:', !!session);
  log.debug('User ID:', session?.user?.id);

  if (session?.user) {
    const permCheck = await checkInfluencerEditPermissions();
    log.debug('Can edit influencers:', permCheck.canEdit);
    log.debug('User role:', permCheck.role);
    log.debug('Permissions:', permCheck.permissions);

    if (permCheck.error) {
      log.error('Permission check error:', permCheck.error);
    }
  }

  console.groupEnd();
}
